// English translations.
//
// Key structure must match the reference locale (zh-TW) exactly —
// src/test/unit/i18n.test.js fails on any missing or extra key.
// To scaffold a new language, run `npm run i18n:template` and fill in the blanks.

const en = {
    app: {
        name: 'NTU IM Student Association',
        fullName: 'NTU Department of Information Management Student Association',
        englishName: 'NTU IM Student Association',
    },

    footer: {
        contact: 'Contact',
        links: 'Links',
        deptSite: 'IM Department',
        ntuSite: 'NTU',
    },

    nav: {
        home: 'Home',
        courseReviews: 'Course Reviews',
        examArchive: 'Past Exams',
        cheatSheets: 'Cheat Sheets',
        about: 'About Us',
        comingSoon: 'Coming soon',
        siteNavigation: 'Site navigation',
        openMenu: 'Open menu',
        login: 'Log in',
        loginOrRegister: 'Log in / Sign up',
        logout: 'Log out',
        joinWorkspace: 'Join the IM Google Workspace',
        feePaid: 'Dues paid',
        feeUnpaid: 'Dues unpaid',
        uploadExam: 'Upload past exam',
        adminPanel: 'User management',
        adminExamManage: 'Past exam management',
        adminCheatSheetManage: 'Cheat sheet management',
        changelog: 'Changelog',
        adminSection: 'Administration',
        joinWorkspaceShort: 'Join Google Workspace',
    },

    preview: {
        viewingAs: 'Viewing as',
        viewingAsSuffix:
            '— permissions and API responses across the site have switched. This mode is read-only; nothing can be changed.',
        stop: 'Exit preview',
        stopping: 'Exiting…',
        someRole: 'a role',
        someUser: 'a member',
    },

    guard: {
        moduleUnavailableTitle: 'This feature is not available yet',
        moduleUnavailableBody: 'You do not have access to this feature',
        comingSoonBody: 'This feature is in the works — stay tuned',
        noPermissionTitle: 'Insufficient permissions',
        noPermissionBody: 'Your account does not have access to this feature',
        adminRequiredTitle: 'Administrator access required',
        adminRequiredBody: 'This page is restricted to administrators',
        paymentRequiredTitle: 'Association dues required',
        paymentRequiredBody:
            'This feature is only available to members who have paid their association dues',
        paymentRequiredHint:
            'Please contact an association officer or pay at the department office',
        backHome: 'Back to home',
        contactUs: 'Contact us',
        notLoggedIn: 'Not logged in',
        welcome: 'Welcome, {{name}}',
    },

    payment: {
        thisFeature: 'This feature',
        title: '{{feature}} requires association dues',
        subtitle: 'Pay your dues to unlock the full site',
        unpaidNotice:
            'Your account <strong>{{username}}</strong> has not paid the association dues yet',
        loginFirst: 'Log in or sign up, then pay your association dues to unlock the full site',
        benefitsTitle: 'What your dues get you',
        benefits: [
            'Discounts on department events (including the genuinely great barbecue)',
            'Access to the Academic Division past-exam archive',
            'Full access to the association website',
        ],
        feeLabel: 'Association dues',
        perFourYears: '/ four years',
        reviewNotice:
            'Payments are verified manually and processed each evening. Once approved, the full site unlocks.',
        contactAssociation: 'Contact the association',
    },

    home: {
        quickAccess: 'Quick access',
        quickLinks: {
            courseReviews: 'Read reviews and course-selection advice from other students',
            examArchiveTitle: 'Past Exam Archive',
            examArchive: 'Download and browse past exams',
            cheatSheetsTitle: 'Cheat Sheets',
            cheatSheets: 'Course summaries and shared notes',
        },
        units: {
            reviews: 'reviews',
            exams: 'past exams',
            cheatSheets: 'cheat sheets',
        },
        banner: {
            loginTitle: 'Log in to unlock the full site',
            loginBody: 'Download past exams, browse cheat sheets and read course reviews',
            unpaidTitle: 'Hi {{name}}, your association dues are unpaid',
            unpaidBody: 'Pay your dues to download past exams and unlock the full site',
            contactToPay: 'Contact us to pay',
        },
    },

    line: {
        title: 'LINE notifications',
        description:
            'Link your LINE account to be notified when something needs review. Only people with the matching permission receive them.',
        notConfigured:
            'LINE notifications are not configured on the server yet — please contact the site maintainer.',
        bound: 'Linked',
        notBound: 'Not linked',
        generateCode: 'Generate linking code',
        regenerateCode: 'Generate a new code',
        codeReady: 'Send the code below to the association LINE account to finish linking.',
        codeExpiresAt: 'Valid until {{time}} — generate a new one if it expires',
        codeFailed: 'Could not generate a linking code',
        unbind: 'Unlink',
        unbound: 'LINE account unlinked',
        unbindFailed: 'Could not unlink your LINE account',
        myBinding: 'My linked account',
        admin: {
            title: 'Linked members',
            description:
                'Remember to unlink outgoing officers during handover — otherwise they keep receiving notifications after they graduate.',
            empty: 'Nobody has linked a LINE account yet.',
            fetchFailed: 'Could not load the list of linked members',
            colUser: 'Member',
            colBoundAt: 'Linked at',
            colNotifies: 'Receives',
            boundAtUnknown: 'Unknown',
            notifiesNone: 'Receives nothing',
            notify: {
                courseReviews: 'Course reviews',
                feedback: 'Feedback',
            },
            unbindTitle: 'Unlink this member',
            confirmUnbind:
                'Unlink the LINE account of "{{name}}"? They will not be told — they will simply stop receiving notifications.',
        },
    },

    feedback: {
        title: 'Send feedback',
        category: 'Category',
        categories: {
            bug: 'Bug report',
            suggestion: 'Feature suggestion',
            other: 'Other',
        },
        body: 'Your feedback',
        bodyHelper: '{{count}} / {{max}} characters (at least 10)',
        submit: 'Send',
        submitting: 'Sending…',
        submitFailed: 'Could not send your feedback',
        sent: 'Thanks — your feedback has been received.',
        sentHint:
            'Because feedback is anonymous we cannot reply individually, but every message is read.',
        anonymityNotice: 'This feedback is anonymous.',
        loginRequired: 'Please log in before sending feedback.',
        loginRequiredWhy: 'Logging in is only used to prevent spam.',
        admin: {
            title: 'Feedback',
            description: 'Read anonymous feedback submitted by users',
            empty: 'No feedback with this status.',
            allStatuses: 'All',
            fetchFailed: 'Could not load feedback',
            updated: 'Feedback updated',
            updateFailed: 'Could not update the feedback',
            deleted: 'Feedback deleted',
            deleteFailed: 'Could not delete the feedback',
            deleteTitle: 'Delete feedback',
            confirmDelete: 'Delete this feedback? This cannot be undone.',
            markRead: 'Mark as read',
            markResolved: 'Mark as resolved',
            editNote: 'Admin note',
            noteHelper: 'Visible to admins only; the sender will never see it.',
            note: 'Admin note',
            anonymityNotice:
                'Feedback carries no sender information — the column does not exist in the database. Do not attempt to correlate timestamps with server logs to identify senders.',
            status: {
                new: 'Unhandled',
                read: 'Read',
                resolved: 'Resolved',
            },
        },
    },

    announcement: {
        dialogTitle: 'Announcements',
        important: 'Important',
        levelInfo: 'Normal',
        dontRemindMe: "Don't remind me again",
        admin: {
            title: 'Announcements',
            description: 'Publish site announcements shown to users on their first visit',
            create: 'New announcement',
            editTitle: 'Edit announcement',
            empty: 'There are no announcements yet.',
            fetchFailed: 'Could not load announcements',
            saved: 'Announcement saved',
            saveFailed: 'Could not save the announcement',
            deleted: 'Announcement deleted',
            deleteFailed: 'Could not delete the announcement',
            deleteTitle: 'Delete announcement',
            confirmDelete: 'Delete the announcement "{{title}}"? This cannot be undone.',
            colTitle: 'Title',
            colState: 'Status',
            colWindow: 'Publish → Expire',
            colActions: 'Actions',
            noLimit: 'None',
            fieldTitle: 'Title',
            fieldBody: 'Body',
            fieldLevel: 'Level',
            fieldEnabled: 'Enabled',
            fieldPublishAt: 'Publish at',
            fieldExpireAt: 'Expire at',
            bodyHelper: 'Plain text; line breaks are preserved.',
            windowHelper:
                'Both are optional — leaving one empty means no limit on that side. Times use your local time zone.',
            state: {
                active: 'Live',
                disabled: 'Disabled',
                scheduled: 'Scheduled',
                expired: 'Expired',
            },
        },
    },
    about: {
        staffTab: 'Our Team',
        contactTab: 'Contact Us',
        coreStaff: 'Core officers',
        directors: 'Division directors',
        // 學年沿用民國年（AY 114），不換算成西元——校內公告與系上文件都用民國年
        academicYearLabel: 'Academic year',
        yearOption: 'AY {{year}}',
        noStaffForYear: 'The AY {{year}} officer roster has not been published yet.',
        officeLabel: 'Office',
        officeAddress:
            'Department of Information Management Office, 7F, College of Management Building I',
    },

    auth: {
        register: 'Sign up',
        loginSubtitle: 'Log in with your student ID or username',
        loginFailed: 'Login failed — check your credentials or network connection',
        usernameOrStudentId: 'Student ID or username',
        password: 'Password',
        confirmPassword: 'Confirm password',
        passwordHelper: 'At least 6 characters',
        passwordMismatch: 'Passwords do not match',
        passwordTooShort: 'Password must be at least 6 characters',
        registerFailed: 'Sign-up failed, please try again later',
        registerSubtitle: 'Join the NTU IM Student Association',
        registerFeeNotice:
            'After signing up, contact the association to pay your dues if you need full access (downloading past exams, uploading cheat sheets, and so on).',
        studentId: 'Student ID',
        studentIdExample: 'e.g. B09705001',
        username: 'Username',
        fullName: 'Full name',
        email: 'Email',
        emailExample: 'e.g. student@ntu.edu.tw',
        noAccount: "Don't have an account?",
        registerNow: 'Sign up now',
        haveAccount: 'Already have an account?',
        loginNow: 'Log in',
        forgotPassword: 'Forgot your password? Contact',
    },

    time: {
        justNow: 'Just now',
        minutesAgo: '{{count}} minutes ago',
        hoursAgo: '{{count}} hours ago',
        daysAgo: '{{count}} days ago',
        monthsAgo: '{{count}} months ago',
        yearsAgo: '{{count}} years ago',
        academicTerm: '{{year}} {{semester}}',
        nthSemester: 'Semester {{n}}',
    },

    validation: {
        email: 'Please enter a valid email address',
        studentId: 'Please enter a valid student ID',
        ratingRange: 'Rating must be between 1 and 5',
        pdfOnly: 'Only PDF files can be uploaded',
        fileTooLarge: 'Files must be 10MB or smaller',
    },

    exam: {
        fetchFailed: 'Could not load past exams',
        payToDownload: 'Pay your association dues to download past exams',
        payToPreview: 'Pay your association dues to preview past exams',
        stats: '{{count}} files · {{downloads}} downloads · {{years}} academic years',
        searchPlaceholder: 'Search by course name or instructor…',
        academicYear: 'AY {{year}}',
        loadingList: 'Loading past exams…',
        unknownProfessor: 'Unknown instructor',
        unknownDate: 'Unknown date',
        preview: 'Preview',
        download: 'Download',
        previewQuestions: 'Preview questions',
        downloadQuestions: 'Download questions',
        previewAnswers: 'Preview answers',
        downloadAnswers: 'Download answers',
        empty: 'No past exams yet',
        emptyHint: 'Ask an administrator to upload past exams',
        noMatch: 'No past exams match your filters',
        noMatchHint: 'Try adjusting your search or filters',
        sort: {
            latest: 'Newest',
            downloads: 'Most downloaded',
        },
        form: {
            basicInfo: 'Basic information',
            year: 'Year',
            examType: 'Exam type',
            examAttempt: 'Exam number',
            updateInfo: 'Update information',
            fileManagement: 'Files',
            currentFiles: 'Current files:',
            questionFile: 'Questions: {{name}}',
            answerFile: 'Answers: {{name}}',
            updateQuestionFile: 'Replace question file (PDF)',
            updateAnswerFile: 'Replace answer file (PDF)',
            removeAnswer: 'Remove answer file',
            cancelRemoveAnswer: 'Keep answer file',
            updateFile: 'Update files',
            codeAndNameRequired: 'Course number and course name are required',
            updateFailed: 'Update failed',
            fileUpdateFailed: 'File update failed',
            pickFileOrRemove: 'Choose a file to upload, or remove the answer file',
            editTitle: 'Edit past exam',
            semester: 'Semester',
        },
    },

    cheatSheet: {
        fetchFailed: 'Could not load cheat sheets',
        downloadFailed: 'Download failed',
        downloadFailedRetry: 'Download failed, please try again later',
        loginToDownload: 'Log in to download cheat sheets',
        loginToPreview: 'Log in to preview cheat sheets',
        loginRequired: 'Please log in',
        loginRequiredBody: 'Log in to browse the cheat sheets',
        stats: '{{count}} files · {{downloads}} downloads · {{courses}} courses',
        searchPlaceholder: 'Search by title, course or content…',
        courseFilter: 'Course',
        popularTags: 'Popular tags',
        loadingList: 'Loading cheat sheets…',
        empty: 'No cheat sheets yet',
        emptyHint: 'Ask an administrator to upload cheat sheets',
        noMatch: 'No cheat sheets match your filters',
        noMatchHint: 'Try adjusting your search or picking a different tag',
        form: {
            title: 'Title',
            description: 'Description',
            tags: 'Tags',
            addTag: 'Add tag',
            add: 'Add',
            updateFilePdf: 'Replace file (PDF)',
            requiredFields: 'Course number, course name and title are required',
            pickFile: 'Please choose a file to upload',
            editTitle: 'Edit cheat sheet',
        },
    },

    examUpload: {
        loginRequired: 'Please log in',
        loginRequiredBody: 'Log in to upload past exams and cheat sheets',
        goToLogin: 'Go to login',
        effectiveFrom: 'Effective from AY 114-1',
        greeting:
            'Hi {{name}} — contribute past exams or cheat sheets to help others and earn a reward',
        viewFullRules: 'View full rules',
        eligibleItems: 'What you can upload',
        namingPattern: 'year_term_course_examType',
        namingExample: '(e.g. 112-1_Programming_Final.pdf)',
        processTitle: 'How it works',
        viewSheetButton: 'View what is already uploaded (Google Sheet)',
        formTitle: 'Upload form',
        openInNewTab: 'Open in a new tab',
        iframeTitle: 'IM past-exam upload form',
        formHint:
            'You can scroll inside the form. If it is too long or displays incorrectly, use "Open in a new tab" at the top right.',
        notice: {
            title: 'Before you upload',
            range: '<b>Last five years</b>: counting back from the current term (AY 114-1 accepts AY 110–114). Anything older does not count towards a reward.',
            answers:
                '<b>Answers</b>: include them if you have them, but <b>do not write your own answers</b> (unless you know they scored full marks).',
            naming: '<b>File naming</b>: <code>{{pattern}}</code> (e.g. 112-1_Programming_Final.pdf)',
            namingWarning:
                '<b>Files that do not follow the naming format are not counted.</b> If questions and answers are separate, upload them as two files.',
        },
        rules: {
            title: 'IM past-exam upload rules',
            s1: {
                title: 'I. You may upload material from the <red>last five years</red>',
                exams: '・<b>Past exams / cheat sheets</b>: <redBold>midterms and finals</redBold> for courses (required, elective or general education)',
                calculus: '・<b>Calculus</b>: <redBold>quizzes</redBold> only',
            },
            s2: {
                title: 'II. Five years = five years back, <red>including the current term</red>',
                example1Label: 'Example 1 ✌',
                example1:
                    'It is AY 114-1 (AY 110–114 accepted). A student uploads 4 statistics exams from AY 111–114 → approved, reward of <b>NT$ 250</b>',
                example2Label: 'Example 2 ✗',
                example2:
                    'It is AY 114-1. A student uploads 4 data-structures exams from 1993–1996 → <b>outside the accepted range</b>, no reward',
            },
            s3: {
                title: 'III. Notes and rewards',
                answers:
                    '・<b>Answers</b>: include them if you have them; <red>do not write your own answers</red> (unless you know they scored full marks)',
                reward: '・<b>Reward</b>: every <b>4 past exams / cheat sheets</b> you upload (excluding department-required courses) earns <b> NT$ 250 </b> once approved',
            },
            s4: {
                title: 'IV. Upload and reward caps ⚠',
                body: 'So that everyone gets a fair chance to contribute, rewards stop once you have submitted <redBold>32 files</redBold> and received <redBold>NT$ 2000</redBold>.',
                exampleLabel: 'Example',
                example:
                    'A student uploads 99 past exams from AY 110–114 plus course reviews → all approved, but the reward is <b>capped at NT$ 2000</b>',
            },
            s5: {
                title: 'V. File requirements',
                examFormat:
                    '・<b>Format</b>: original exam files or scans of the questions, uploaded as a PDF once organised',
                examQuality: '・<b>Quality</b>: clear and legible text',
                cheatSheetFormat:
                    '・<b>Format</b>: handwritten or typed, uploaded as a PDF once organised',
                cheatSheetQuality:
                    '・<b>Quality</b>: clear, tidy and legible, genuinely useful for revision (the Academic Division reviews this)',
            },
            s6: {
                title: 'VI. Upload process',
                step1Title: '1. Check what is already uploaded',
                step1Body:
                    'To avoid duplicates, use the "View what is already uploaded" button above to check the Google Sheet first',
                step2Title: '2. Submit the form',
                step2Body:
                    'Use your NTU address (@ntu.edu.tw). Name files as <code>{{pattern}}</code> (e.g. 112-1_Programming_Final.pdf). <red>Files that do not follow the format are not counted.</red> If questions and answers are separate, upload them as two files',
                step3Title: '3. Wait for review',
                step3Body:
                    'Once approved and 4 files have accumulated, the Academic Division notifies everyone and pays the rewards at the same time',
            },
        },
        stats: {
            range: {
                value: 'Last 5 years',
                label: 'Eligible range',
                hint: 'For AY 114-1: AY 110–114 are accepted',
            },
            batch: {
                value: '4 per batch',
                label: 'Review unit',
                hint: 'Rewards are paid once 4 files accumulate',
            },
            reward: {
                value: 'NT$ 250',
                label: 'Per batch',
                hint: 'Paid once the batch is approved',
            },
            cap: {
                value: '32 files / $2000',
                label: 'Per-person cap',
                hint: 'No further rewards after the cap',
            },
        },
        categories: {
            exam: {
                title: 'Past exams',
                desc: 'Midterms and finals for courses (required, elective or general education)',
                bullets: [
                    'Original exam files or scans of the questions',
                    'Upload as a PDF once organised',
                    'Clear and legible text',
                    'Calculus is the exception: only quizzes are accepted',
                ],
            },
            cheatSheet: {
                title: 'Cheat sheets',
                desc: 'Course summaries that help others revise',
                bullets: [
                    'Handwritten or typed, either is fine',
                    'Upload as a PDF once organised',
                    'Clear, tidy and legible text',
                    'The Academic Division decides what is accepted',
                ],
            },
        },
        steps: {
            checkList: {
                label: 'Check what is already uploaded',
                desc: 'Check the Google Sheet first so you do not upload a duplicate',
            },
            fillForm: {
                label: 'Submit the form with your files',
                desc: 'Use your @ntu.edu.tw address and follow the file naming rules',
            },
            awaitReview: {
                label: 'Wait for the Academic Division',
                desc: 'Once 4 files accumulate and pass review, the division notifies you and pays the reward',
            },
        },
    },

    manage: {
        deleteFailed: 'Deletion failed',
        deleteIrreversible: 'This cannot be undone — the file will be permanently deleted.',
        adjustSearch: 'Try adjusting your search',
        noTags: 'No tags',
        examDescription: 'Manage every past exam in the system — view, download or delete',
        examDeleted: 'Past exam deleted',
        examInfoUpdated: 'Past exam details updated',
        examFileUpdated: 'Past exam files updated',
        uploadExamFirst: 'Upload a past exam first',
        confirmDeleteExam: 'Delete "{{name}}"?',
        cheatSheetDescription: 'Manage every cheat sheet in the system — view, download or delete',
        cheatSheetDeleted: 'Cheat sheet deleted',
        cheatSheetInfoUpdated: 'Cheat sheet details updated',
        cheatSheetFileUpdated: 'Cheat sheet file updated',
        uploadCheatSheetFirst: 'Upload a cheat sheet first',
        confirmDeleteCheatSheet: 'Delete "{{name}}"?',
        // Table column headers for the two manage pages. These used to be
        // hardcoded Chinese in the JSX, so the whole header row stayed Chinese in English.
        columns: {
            courseInfo: 'Course',
            professor: 'Professor',
            examInfo: 'Exam',
            fileInfo: 'File',
            uploader: 'Uploaded by',
            uploadDate: 'Uploaded',
            downloads: 'Downloads',
            actions: 'Actions',
            title: 'Title',
            description: 'Description',
            tags: 'Tags',
            courseName: 'Course',
            examType: 'Type',
        },
        confirmDeleteExamTitle: 'Delete this past exam?',
        confirmDeleteCheatSheetTitle: 'Delete this cheat sheet?',
    },

    admin: {
        consoleTitle: 'Admin console',
        consoleSubtitle: 'Jump straight to the workspace you need — no swipe tabs.',
        noAccess: 'You do not have permission to view this page',
        debugInfo: 'Debug info: username={{username}}, role={{role}}',
        previewSwitchFailed: 'Could not switch preview identity',
        uploadCheatSheet: 'Upload cheat sheet',
        sections: {
            users: 'Search, edit, reset passwords',
            roles: 'Create roles, adjust permissions and members',
            modules: 'Choose which roles each feature is open to',
            examManage: 'Search, preview, delete',
            cheatSheetManage: 'Review content and clean up',
            examUpload: 'Add question and answer files',
            cheatSheetUpload: 'Create course summaries',
        },
        preview: {
            selfHint: 'This is your own account',
            asUser: 'View the whole site as "{{name}}" (read-only)',
        },
        users: {
            fetchFailed: 'Could not load user data',
            updated: 'User details updated',
            deleted: 'User {{name}} deleted',
            deleteFailed: 'Could not delete the user',
            searchPlaceholder: 'Search by username, name, email or student ID',
            paymentFilter: 'Dues',
            showing: 'Showing {{shown}} of {{total}} users',
            clearFilters: 'Clear filters',
            studentIdLine: 'Student ID: {{value}}',
            registeredLine: 'Registered: {{value}}',
            noEmail: 'No email on file',
            notProvided: 'Not provided',
            paid: 'Paid',
            unpaid: 'Unpaid',
            resetPassword: 'Reset password',
            newPassword: 'New password',
            enterNewPassword: 'Please enter a new password',
            passwordUpdated: 'Password updated',
            passwordUpdateFailed: 'Could not update the password',
            confirmChange: 'Confirm change',
            cannotDeleteSelf: 'You cannot delete your own account',
            deleteUser: 'Delete user',
            deleteThisUser: 'Delete this user',
            editThisUser: 'Edit this user',
            confirmDelete: 'Delete user "{{name}}"?',
            deleteIrreversible:
                "This cannot be undone — all of the user's data will be permanently deleted.",
            detailTitle: 'User details',
            adjustFilters: 'Try adjusting your search terms or filters.',
            empty: 'No users to show.',
            colUser: 'User',
            colContact: 'Contact & campus details',
            colStatus: 'Status',
            roleFilter: 'Role',
            noRole: 'No role',
            changePasswordTitle: 'Change password',
            confirmDeleteTitle: 'Delete this user?',
        },
        upload: {
            pdfOnly: 'Only PDF files can be uploaded',
            tooLarge: 'Files must be 50MB or smaller',
            courseCodeRequired: 'Please enter a course number',
            courseNameRequired: 'Please enter a course name',
            professorRequired: 'Please enter the instructor name',
            yearRequired: 'Please choose a year',
            questionFileRequired: 'Please choose a question PDF to upload',
            titleRequired: 'Please enter a title',
            descriptionRequired: 'Please enter a description',
            tagRequired: 'Please add at least one tag',
            fileRequired: 'Please choose a PDF to upload',
            failed: 'Upload failed',
            failedRetry: 'Upload failed, please try again later',
            uploading: 'Uploading…',
            examSuccess: 'Past exam uploaded',
            cheatSheetSuccess: 'Cheat sheet uploaded',
            courseCodeExample: 'e.g. IM1001',
            courseNameExample: 'e.g. Introduction to Information Management',
            professorLabel: 'Instructor name',
            professorExample: 'e.g. Prof. Wang',
            yearRocLabel: 'Year (ROC)',
            questionFileLabel: 'Question file',
            answerFileLabel: 'Answer file',
            optionalTag: '(optional)',
            pickQuestionPdf: 'Choose question PDF',
            pickAnswerPdf: 'Choose answer PDF',
            pickPdf: 'Choose PDF file',
            selected: 'Selected: {{name}}',
            answerOptionalHint: 'Not every past exam has answers — this can be left empty',
            cheatSheetTitleLabel: 'Cheat sheet title',
            cheatSheetTitleExample: 'e.g. Final exam summary',
            cheatSheetDescExample: 'Briefly describe the contents…',
            tagPlaceholder: 'Type a tag then press Enter or click Add',
            semesterLabel: 'Semester',
            examTypeLabel: 'Exam type',
            examTypeMidterm: 'Midterm',
            examTypeFinal: 'Final',
            examTypeQuiz: 'Quiz',
        },
        manage: {
            questionLine: 'Questions: {{name}}',
            answerLine: 'Answers: {{name}}',
        },
        roles: {
            title: 'Role management',
            label: 'Roles',
            description:
                'A user can hold several roles; their permissions are the union of all of them',
            create: 'New role',
            editTitle: 'Edit role',
            fetchFailed: 'Could not load roles',
            saved: 'Role saved',
            saveFailed: 'Could not save the role',
            deleted: 'Role deleted',
            deleteFailed: 'Could not delete the role',
            builtin: 'Built-in',
            auto: 'Auto-granted',
            builtinNotDeletable: 'Built-in roles cannot be deleted',
            memberCount: '{{count}} members',
            autoComputed: ' (derived from dues status)',
            allPermissions: 'All permissions',
            none: 'No roles',
            noneAssignable: 'No assignable roles',
            loadingOrNoAccess: 'Loading, or you cannot read roles',
            memberAutoHint:
                '"Member" is granted automatically from dues status and cannot be assigned here',
            keyLabel: 'Key',
            keyLocked: 'The key cannot be changed after creation',
            keyHelper: 'Must start with a letter; letters, digits and underscores only',
            nameLabel: 'Name',
            descLabel: 'Description',
            colorLabel: 'Colour',
            priorityLabel: 'Sort priority',
            priorityHelper: 'Higher numbers come first',
            deleteWarning:
                'This will delete "{{name}}". The {{count}} users holding it will lose the matching permissions. This cannot be undone.',
            permissionsLabel: 'Permissions',
            allPermissionsLocked:
                'This role holds every permission and cannot be adjusted item by item',
            deleteTitle: 'Delete this role?',
        },
        modules: {
            title: 'Module management',
            description:
                'Control who each feature is open to. Once set to "Restricted", only allow-listed roles or users can use it; administrators always can, so a feature can be tested in production before going public.',
            fetchFailed: 'Could not load module settings',
            saved: 'Module settings updated',
            saveFailed: 'Could not update module settings',
            visibilityLabel: 'Availability',
            allowedRoles: 'Allowed roles',
            showComingSoon: 'Show as "Coming soon"',
            hideCompletely: 'Hide completely',
            visibilityPublic: 'Public (everyone)',
            visibilityRestricted: 'Restricted (allowlist)',
        },
    },

    changelog: {
        summary: '{{count}} releases · continuously updated from {{from}} to {{to}}',
        currentVersion: 'Current version: v{{version}}',
        latest: 'Latest',
    },

    error: {
        boundaryTitle: 'Something went wrong',
        boundaryBody:
            'An error occurred while loading the page. Please refresh, or contact an administrator.',
        reload: 'Reload',
    },

    calendar: {
        title: 'NTU Academic Calendar',
        fullCalendar: 'Full calendar',
        prevMonth: 'Previous month',
        nextMonth: 'Next month',
        yearMonth: '{{month}}/{{year}}',
        weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        weekdayShort: '{{day}}',
        legendDeadline: 'Key deadline',
        legendHoliday: 'Holiday',
        legendTermEvent: 'Term event',
        upcoming: 'Upcoming',
        noUpcoming: 'Nothing coming up',
    },

    feeStatus: {
        pleaseLogin: 'Please log in',
        admin: 'Administrator',
        paid: 'Association dues paid',
        unpaid: 'Association dues unpaid',
    },

    common: {
        cancel: 'Cancel',
        confirm: 'Confirm',
        save: 'Save',
        delete: 'Delete',
        edit: 'Edit',
        close: 'Close',
        search: 'Search',
        submit: 'Submit',
        loading: 'Loading…',
        unknown: 'Unknown',
        approve: 'Approve',
        reject: 'Reject',
        all: 'All',
        sort: 'Sort',
        language: 'Language',
        searchPlaceholder: 'Search…',
    },

    courseReview: {
        pageTitle: 'Course Reviews',
        loadingReviews: 'Loading course reviews…',

        viewMode: {
            feed: 'All Reviews',
            mine: 'My Reviews',
        },

        semester: {
            1: 'Fall',
            2: 'Spring',
            summer: 'Summer',
        },

        // Suffix used in the short academic-term format (e.g. 115-2, 115-Su)
        academicTermSuffix: {
            1: '1',
            2: '2',
            summer: 'Su',
        },

        status: {
            pending: 'Pending',
            approved: 'Approved',
            rejected: 'Rejected',
        },

        metrics: {
            quality: 'Quality',
            difficulty: 'Difficulty',
            sweetness: 'Grading',
            usefulness: 'Usefulness',
        },

        metricHints: {
            quality: 'Overall teaching quality of this course',
            difficulty: 'How demanding the lectures and exams are',
            sweetness: 'How generously the course is graded — higher means easier to score well',
            usefulness: 'How useful the material is in practice or for further study',
        },

        detailField: {
            professor: 'Instructor',
            courseCode: 'Course No.',
            academicTerm: 'Term',
        },

        // Index 0 is intentionally blank; ratings map to the nearest whole number 1–5
        metricTexts: {
            quality: ['', 'Very poor', 'Poor', 'Average', 'Good', 'Excellent'],
            difficulty: ['', 'Very easy', 'Easy', 'Average', 'Hard', 'Very hard'],
            sweetness: ['', 'Very harsh', 'Harsh', 'Average', 'Generous', 'Very generous'],
            usefulness: ['', 'Not useful', 'Slightly useful', 'Average', 'Useful', 'Very useful'],
        },

        sort: {
            latestPost: 'Newest',
            ratingHighest: 'Highest rated',
            ratingLowest: 'Lowest rated',
        },

        searchPlaceholder: {
            feed: 'Search courses or instructors…',
        },

        emptyState: {
            noReviewsYet: 'No course reviews yet',
            noMatchingReviews: 'No reviews match your filters',
            beFirst: 'Be the first to share your experience',
            adjustSearchOrFilter: 'Try adjusting your search or filters',
            noReviewsMine: "You haven't written any reviews yet",
            shareYourExperience: 'Share your experience to help others choose their courses',
            writeFirstReview: 'Write your first review',
            loginToWrite: 'Log in to share your course experience',
        },

        totalReviewCount: '{{count}} reviews',
        writeReview: 'Write a review',
        editReview: 'Edit review',
        newReviewTitle: 'New course review',
        professorFilterLabel: 'Instructor',
        confirmDeleteReview: 'Delete this review?',
        reviewedBy: 'Reviewed by: {{name}}',
        rejectReasonLabel: 'Reason for rejection: {{reason}}',
        clickForDetail: 'Click the card to read the full review',
        viewDetailTitle: 'Review details',
        reviseAndResubmit: 'Edit and resubmit',

        form: {
            courseName: 'Course name',
            courseCode: 'Course No.',
            professor: 'Instructor',
            academicTerm: 'Term',
            allTerms: 'All',
            courseNameHelper:
                'Pick a term first, then type a name and choose from the list — the instructor and course number fill in automatically. You can also type them in manually.',
            termRequiredForManualEntry:
                'When the term is set to "All", pick a course from the list. To type it in manually, choose a specific term first.',
            searchTruncatedHint:
                'Too many matches to show them all. Type a more complete course name, or type the instructor’s name instead.',
            courseContent: 'Course content',
            teachingMethod: 'Teaching style',
            assignmentExamFormat: 'Assignments and exams',
            gradingBreakdown: 'Grade breakdown',
            comment: 'Your review',
            notRatedYet: 'Not rated',
            anonymous: 'Post anonymously',
            lockedFieldsHint:
                'Course, instructor and term cannot be changed after submission. If something is wrong, delete the review and create a new one.',
            courseContentHelper:
                '{{count}} / {{max}} characters (at least {{min}}; saved as a draft automatically)',
            requiredFieldHelper: '{{count}} / {{max}} characters (at least {{min}})',
            commentHelper:
                '{{count}} / {{max}} characters (at least {{min}}; published only after approval; saved as a draft automatically)',
            basicInfoRequired: 'Course number, course name and instructor are required',
            ratingsIncomplete: 'Please complete all ratings',
            draftRestored: 'Restored your unfinished draft',
            dismissDraftNotice: 'Got it',
            clearDraft: 'Discard draft',
            saveChanges: 'Save changes',
            submitReview: 'Submit review',
            resubmit: 'Resubmit',
        },

        quota: {
            label: 'Reward quota',
            chip: '{{tier}} {{used}}/{{limit}}',
            full: 'Full',
            remaining: '{{count}} left',
            helper: 'Each approved review earns {{amount}} NTD. Every course has a limited number of paid slots (3 for IM required courses, 2 for IM electives, 1 otherwise). You can still submit once a course is full — it just will not earn a reward.',
            fullWarning:
                'This course has already used all {{limit}} of its reward slots. You can still submit, and your review will still be published — it just will not earn a reward.',
            overQuota: 'Over quota',
            overQuotaHint:
                'Earlier submissions have taken every slot for this course, so this review earns no reward',
            eligible: 'Within quota',
            tier: {
                imRequired: 'IM required',
                imElective: 'IM elective',
                other: 'Other',
            },
        },

        payout: {
            title: 'Reward payouts',
            description:
                'Track which course-review rewards have been paid. Only approved reviews appear here.',
            searchPlaceholder: 'Search by name, student ID, course or instructor…',
            // Three payout states. "Pending" rather than "Unpaid": the latter reads
            // almost the same as "Declined" but means the opposite (not decided yet
            // vs decided not to pay), and the treasurer will misread it.
            pending: 'Pending',
            paid: 'Paid',
            declined: 'Declined',
            empty: 'No matching records',
            recipient: 'Recipient',
            studentId: 'Student ID',
            course: 'Course',
            submittedAt: 'Submitted',
            status: 'Status',
            paidBy: 'Paid by',
            action: 'Actions',
            markPaid: 'Mark as paid',
            markDeclined: 'Mark as declined',
            markPending: 'Move back to pending',
            exportCsv: 'Export CSV',
            anonymousTag: 'Anonymous',
            anonymousHint:
                'This review is shown anonymously, but the reward still goes to this person',
            roleTag: 'Treasurer',
            roleOn: 'Can manage reward payouts',
            roleOff: 'No payout permission',
            markPaidSuccess: 'Marked as paid',
            markDeclinedSuccess: 'Marked as declined',
            markPendingSuccess: 'Moved back to pending',
            updateFailed: 'Could not update payout status',
            fetchFailed: 'Could not load the payout list',
            exportFailed: 'Export failed',
        },

        admin: {
            title: 'Course review moderation',
            description:
                'Course reviews earn a reward and must be approved before they appear publicly. You can also delete any review here.',
            searchPlaceholder: 'Search by course name, course number or instructor…',
            noMatchingReviews: 'No reviews match your filters',
            noMatchingSearchReviews: 'No reviews match your search',
            countLabel: '{{count}} total',
            submittedAt: 'Submitted: {{time}}',
            rejectDialogTitle: 'Reject this review',
            rejectReasonInput: 'Reason for rejection',
            rejectReasonHelper: 'Shown to the author so they know what to fix',
            confirmReject: 'Confirm rejection',
            deleteDialogTitle: 'Delete this review?',
            deleteDialogBody: 'This cannot be undone — the review will be permanently deleted.',
            confirmDelete: 'Confirm deletion',
            approveSuccess: 'Review approved and published',
            approveFailed: 'Approval failed',
            rejectSuccess: 'Review rejected',
            rejectFailed: 'Rejection failed',
            deleteSuccess: 'Review deleted',
            deleteFailed: 'Deletion failed',
        },
    },

    // Looked up by the errorCode the backend returns
    errors: {
        LINE_BAD_SIGNATURE: 'Signature verification failed',
        LINE_NOT_CONFIGURED: 'LINE notifications are not configured',
        LINE_BINDING_FETCH_FAILED: 'Could not load your LINE linking status',
        LINE_BINDING_CODE_FAILED: 'Could not generate a linking code',
        LINE_UNBIND_FAILED: 'Could not unlink your LINE account',
        LINE_BINDINGS_FETCH_FAILED: 'Could not load the list of linked members',
        LINE_UNBIND_OTHER_FAILED: 'Could not unlink that member',
        RATE_LIMITED: 'Too many requests — please try again in {{minutes}} minutes',
        REVIEW_RATE_LIMITED:
            'You are submitting reviews too quickly — please try again in {{minutes}} minutes',
        FEEDBACK_RATE_LIMITED:
            'You are sending feedback too quickly — please try again in {{minutes}} minutes',
        COURSE_NOT_IN_CATALOG:
            'That course was not found — please pick one from the search results',
        REVIEW_LIMIT_EXCEEDED: 'You have reached the maximum number of reviews',
        FEEDBACK_BODY_REQUIRED: 'Feedback is required and must be 10-2000 characters',
        FEEDBACK_CATEGORY_INVALID: 'Invalid feedback category',
        FEEDBACK_STATUS_INVALID: 'Invalid feedback status',
        FEEDBACK_NOT_FOUND: 'That feedback no longer exists',
        FETCH_FEEDBACK_FAILED: 'Could not load feedback',
        CREATE_FEEDBACK_FAILED: 'Could not send your feedback',
        UPDATE_FEEDBACK_FAILED: 'Could not update the feedback',
        DELETE_FEEDBACK_FAILED: 'Could not delete the feedback',
        ANNOUNCEMENT_NOT_FOUND: 'That announcement no longer exists',
        ANNOUNCEMENT_TITLE_REQUIRED: 'A title is required and must be 200 characters or fewer',
        ANNOUNCEMENT_BODY_REQUIRED: 'The announcement body is required',
        ANNOUNCEMENT_LEVEL_INVALID: 'Invalid announcement level',
        ANNOUNCEMENT_TIME_INVALID: 'The publish or expire time is not a valid date',
        ANNOUNCEMENT_INVALID_WINDOW: 'The expire time must be later than the publish time',
        FETCH_ANNOUNCEMENTS_FAILED: 'Could not load announcements',
        CREATE_ANNOUNCEMENT_FAILED: 'Could not create the announcement',
        UPDATE_ANNOUNCEMENT_FAILED: 'Could not update the announcement',
        DELETE_ANNOUNCEMENT_FAILED: 'Could not delete the announcement',
        DISMISS_ANNOUNCEMENT_FAILED: 'Could not save your preference',
        COURSE_CODE_REQUIRED: 'Course number is required',
        COURSE_NAME_REQUIRED: 'Course name is required',
        PROFESSOR_REQUIRED: 'Instructor is required',
        YEAR_INVALID: 'Please enter a valid year',
        SEMESTER_REQUIRED: 'Please choose a term',
        QUALITY_RANGE: 'Quality must be between 0.5 and 5',
        DIFFICULTY_RANGE: 'Difficulty must be between 0.5 and 5',
        SWEETNESS_RANGE: 'Grading must be between 0.5 and 5',
        USEFULNESS_RANGE: 'Usefulness must be between 0.5 and 5',
        COURSE_CONTENT_LENGTH: 'Course content is required and must be {{min}}-{{max}} characters',
        TEACHING_METHOD_LENGTH: 'Teaching style is required and must be {{min}}-{{max}} characters',
        ASSIGNMENT_EXAM_FORMAT_LENGTH:
            'Assignments and exams are required and must be {{min}}-{{max}} characters',
        GRADING_BREAKDOWN_LENGTH:
            'Grade breakdown is required and must be {{min}}-{{max}} characters',
        COMMENT_LENGTH: 'Your review is required and must be {{min}}-{{max}} characters',
        TERM_NOT_REVIEWABLE: 'Final exams for that term are not over yet, so it cannot be reviewed',
        DUPLICATE_REVIEW: 'You have already reviewed this course (same term, same instructor)',
        REVIEW_NOT_FOUND: 'Review not found',
        NO_PERMISSION_EDIT: 'You do not have permission to edit this review',
        NO_PERMISSION_DELETE: 'You do not have permission to delete this review',
        CREATE_FAILED: 'Could not create the review',
        UPDATE_FAILED: 'Could not update the review',
        DELETE_FAILED: 'Could not delete the review',
        FETCH_FAILED: 'Could not load reviews',
        FETCH_STATS_FAILED: 'Could not load statistics',
        FETCH_LIST_FAILED: 'Could not load the review list',
        STATUS_INVALID: 'Status must be either approved or rejected',
        REJECT_REASON_REQUIRED: 'Please give a reason when rejecting',
        REVIEW_STATUS_UPDATE_FAILED: 'Could not update the review status',
        PAYOUT_STATUS_INVALID: 'Payout status must be pending, paid or declined',
        PAYOUT_REQUIRES_APPROVED: 'Only approved reviews can be marked as paid',
        PAYOUT_OVER_QUOTA:
            'This course has no reward slots left, or an earlier submission is still awaiting moderation',
        PAYOUT_UPDATE_FAILED: 'Could not update payout status',
        FETCH_PAYOUTS_FAILED: 'Could not load the payout list',
        EXPORT_PAYOUTS_FAILED: 'Could not export the payout list',
        GENERIC_SUBMIT_FAILED: 'Submission failed, please try again later',

        // ---- 後端 errorCode（階段 3 補齊）----
        MODULE_NOT_AVAILABLE: 'This feature is not available yet',
        ADMIN_ROLE_KEEPS_ALL: 'The administrator role must keep all permissions',
        ANSWER_FILE_NOT_FOUND: 'Answer file not found',
        AUTH_EXPIRED: 'Authentication expired',
        AUTH_FAILED: 'Authentication failed',
        AUTH_REFRESH_INVALID: 'Invalid refresh token',
        AUTH_REFRESH_MISSING: 'No refresh token provided',
        AUTH_TOKEN_EXPIRED: 'Your session has expired',
        AUTH_TOKEN_INVALID: 'Invalid authentication token',
        AUTH_TOKEN_MISSING: 'No authentication token provided',
        AUTO_ROLE_NOT_ASSIGNABLE:
            '"{{name}}" is granted automatically from dues status and cannot be assigned manually',
        BUILTIN_ROLE_NOT_DELETABLE: 'Built-in roles cannot be deleted',
        CANNOT_DELETE_LAST_ADMIN: 'The last administrator cannot be deleted',
        CANNOT_DELETE_SELF: 'You cannot delete your own account',
        CANNOT_REMOVE_LAST_ADMIN: 'The last administrator cannot be removed',
        CHANGE_PASSWORD_FAILED: 'Could not change the password',
        CHEATSHEET_FILE_NOT_FOUND: 'Cheat sheet file not found',
        CHEATSHEET_NOT_FOUND: 'Cheat sheet not found',
        COURSE_CODE_EMPTY: 'Course number cannot be empty',
        COURSE_NAME_EMPTY: 'Course name cannot be empty',
        CREATE_ROLE_FAILED: 'Could not create the role',
        CREDENTIALS_INVALID: 'Incorrect username or password',
        DATABASE_ERROR: 'Database error',
        DELETE_FAILED_GENERIC: 'Deletion failed',
        DELETE_ROLE_FAILED: 'Could not delete the role',
        DELETE_USER_FAILED: 'Could not delete the user',
        DOWNLOAD_FAILED: 'Download failed',
        DUPLICATE_DATA: 'Duplicate data',
        EMAIL_INVALID: 'Please enter a valid email address',
        EMAIL_TAKEN: 'That email address is already in use',
        EXAM_ATTEMPT_RANGE: 'Exam number must be between 1 and 3',
        EXAM_FILE_NOT_FOUND: 'Past exam file not found',
        EXAM_HAS_NO_ANSWER: 'This past exam has no answer file',
        EXAM_NOT_FOUND: 'Past exam not found',
        EXAM_TYPE_REQUIRED: 'Please choose an exam type',
        FEE_STATUS_INVALID: 'Please provide a valid dues status',
        FETCH_CHEATSHEETS_FAILED: 'Could not load cheat sheets',
        FETCH_CONTRIBUTIONS_FAILED: 'Could not load contribution statistics',
        FETCH_EXAMS_FAILED: 'Could not load past exams',
        FETCH_MEMBERS_FAILED: 'Could not load members',
        FETCH_MODULES_FAILED: 'Could not load the module list',
        FETCH_MODULE_SETTINGS_FAILED: 'Could not load module settings',
        FETCH_PROFILE_FAILED: 'Could not load your profile',
        FETCH_ROLES_FAILED: 'Could not load roles',
        FETCH_USER_FAILED: 'Could not load user data',
        FETCH_USER_LIST_FAILED: 'Could not load the user list',
        FIELD_ALREADY_TAKEN: 'That {{field}} is already registered',
        FILE_NOT_FOUND: 'File not found',
        FILE_REQUIRED: 'Please choose a file to upload',
        FILE_TOO_LARGE: 'The file exceeds the size limit',
        FILE_UPDATE_FAILED: 'File update failed',
        FULL_NAME_REQUIRED: 'Full name is required',
        IDENTIFIER_REQUIRED: 'Please enter your student ID or username',
        INTERNAL_ERROR: 'Internal server error',
        LOGIN_FAILED: 'Login failed, please try again later',
        MODULE_NOT_FOUND: 'Module not found',
        NAME_REQUIRED: 'Name is required',
        NEW_PASSWORD_REQUIRED: 'Please provide a new password',
        NEW_PASSWORD_TOO_SHORT: 'The new password must be at least 6 characters',
        NO_PERMISSION_DELETE_CHEATSHEET: 'You do not have permission to delete this cheat sheet',
        NO_PERMISSION_DELETE_EXAM: 'You do not have permission to delete this past exam',
        NO_PERMISSION_EDIT_CHEATSHEET: 'You do not have permission to edit this cheat sheet',
        NO_PERMISSION_EDIT_EXAM: 'You do not have permission to edit this past exam',
        OLD_PASSWORD_INVALID: 'The current password is incorrect',
        OPERATION_NOT_ALLOWED: 'You are not allowed to perform this action',
        PASSWORD_REQUIRED: 'Please enter your password',
        PASSWORD_TOO_SHORT: 'Password must be at least 6 characters',
        PERMISSION_DENIED: 'Insufficient permissions',
        PREVIEW_BAD_TARGET: 'Invalid preview target',
        PREVIEW_ERROR: 'Identity preview failed',
        PREVIEW_FAILED: 'Preview failed, please try again later',
        PREVIEW_FORBIDDEN: 'You do not have permission to use identity preview',
        PREVIEW_READ_ONLY: 'Preview mode is read-only — exit preview before making changes',
        PREVIEW_TARGET_MISSING: 'Preview target not found',
        QUESTION_FILE_NOT_FOUND: 'Question file not found',
        QUESTION_FILE_REQUIRED: 'Please choose a question file to upload',
        REGISTER_FAILED: 'Sign-up failed, please try again later',
        RESOURCE_NOT_FOUND: 'Resource not found',
        ROLE_KEY_FORMAT:
            'The key may only contain letters, digits and underscores, and must start with a letter',
        ROLE_KEY_TAKEN: 'That role key already exists',
        ROLE_LIST_REQUIRED: 'Please provide a list of roles',
        ROLE_NOT_FOUND: 'Role not found',
        ROLE_VALUE_INVALID: 'Please provide a valid role',
        STUDENT_ID_REQUIRED: 'Student ID is required',
        TITLE_EMPTY: 'Title cannot be empty',
        TITLE_REQUIRED: 'Title is required',
        TOO_MANY_FILES: 'Too many files',
        UNEXPECTED_FIELD: 'Unexpected form field',
        UPDATE_FAILED_GENERIC: 'Update failed',
        UPDATE_FEE_STATUS_FAILED: 'Could not update dues status',
        UPDATE_MODULE_SETTINGS_FAILED: 'Could not update module settings',
        UPDATE_PASSWORD_FAILED: 'Could not update the password',
        UPDATE_PROFILE_FAILED: 'Could not update your profile',
        UPDATE_ROLE_FAILED: 'Could not update the role',
        UPLOAD_ERROR: 'Upload error: {{message}}',
        UPLOAD_FAILED: 'Upload failed',
        USERNAME_TOO_SHORT: 'Username must be at least 3 characters',
        USER_NOT_FOUND: 'User not found',
        VALIDATION_FAILED: 'Validation failed',
        NETWORK_FAILED: 'Connection failed — please check your network',
        REQUEST_TIMEOUT: 'The request timed out — please check your network and try again',
        PAYMENT_REQUIRED: 'This feature requires paying the association fee',
    },
};

export default en;
